// Assignments.jsx
import React, { useEffect, useState } from "react";
import {
  useGetAssignmentsForStudentQuery,
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
} from "../../../graphql/generated.tsx";
import { Alert, Button } from "flowbite-react";
import OpeningUI from "../../../components/OpeningUI";
import { FaCalendarAlt, FaSearch } from "react-icons/fa";
import formatDate from "../../Admin/Helpers/formatDate.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import StudentLayout from "../../Student/Layout/index.jsx";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ACCENTS = [
  { bar: "#8A5A2B", pillBg: "#F3E8DA", pillText: "#8A5A2B", pillBgHover: "#EAD9C3" },
  { bar: "#0F6E56", pillBg: "#E1F5EE", pillText: "#0F6E56", pillBgHover: "#C9EADF" },
  { bar: "#3C3489", pillBg: "#EEEDFE", pillText: "#3C3489", pillBgHover: "#E0DFFE" },
];

const getRowAccent = (globalIndex, rowSize = 3) => {
  const rowIndex = Math.floor(globalIndex / rowSize);
  const indexInRow = globalIndex % rowSize;
  // Shuffle 3 accents per row (deterministic) so the first card isn't always the same color.
  const permutations = [
    [0, 1, 2],
    [1, 2, 0],
    [2, 0, 1],
    [0, 2, 1],
    [2, 1, 0],
    [1, 0, 2],
  ];
  const order = permutations[rowIndex % permutations.length];
  return ACCENTS[order[indexInRow] ?? 0];
};

const CalSVG = () => (
  <svg width="14" height="14" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 1.5v2M8.5 1.5v2M1.5 5.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ArrowSVG = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="#667085" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockSVG = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M7 4.2V7.2L9 8.3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #EAECF0",
  boxSizing: "border-box",
};

const AssignmentCard = ({ assignment, globalIndex, onNavigate }) => {
  const accent = getRowAccent(globalIndex, 3);
  const mildTopBar = `${accent.bar}99`;
  const [isHovered, setIsHovered] = useState(false);
  const isCompleted = assignment.completed;
  const dueMs = assignment.due_date ? Date.parse(assignment.due_date) : null;
  const diffMs = dueMs ? dueMs - Date.now() : 0;
  const isOverdue = !isCompleted && diffMs <= 0;
  const minutesAbs = dueMs ? Math.max(0, Math.ceil(Math.abs(diffMs) / (1000 * 60))) : null;
  const hoursAbs = minutesAbs !== null ? Math.ceil(minutesAbs / 60) : null;
  const daysAbs = hoursAbs !== null ? Math.ceil(hoursAbs / 24) : null;
  const usesMinutes = minutesAbs !== null ? minutesAbs < 60 : false;
  const usesDays = hoursAbs !== null ? hoursAbs >= 24 : false;
  const magnitude = usesDays ? daysAbs : usesMinutes ? minutesAbs : hoursAbs;
  const unit = usesDays
    ? (daysAbs === 1 ? " day" : " days")
    : usesMinutes
      ? (minutesAbs === 1 ? " min" : " mins")
      : "h";
  const timePillLabel = isCompleted
    ? null
    : dueMs
      ? isOverdue
        ? `${magnitude}${unit} overdue`
        : `${magnitude}${unit} left`
      : "No due";
  const timeColor = accent.bar;

  const dynamicCardStyle = {
    ...cardStyle,
    transform: "translateY(0)",
    boxShadow: "none",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  };

  const arrowContainerStyle = {
    width: 27,
    height: 27,
    borderRadius: "50%",
    background: isHovered ? accent.pillBgHover : "#F9FAFB",
    border: "1px solid #EAECF0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.2s ease-in-out",
  };

  return (
    <div
      onClick={() => onNavigate(`/assignments/${assignment.id}`)}
      style={dynamicCardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          height: 7,
          background: mildTopBar,
          width: "100%",
          flexShrink: 0,
        }}
      />

      <div style={{ padding: "20px 18px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <p style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: "#101828",
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {assignment.name}
          </p>
        </div>

        <p style={{
          margin: 0,
          fontSize: 13,
          color: "#667085",
          lineHeight: 1.6,
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {assignment.short_description || "Take this assessment based on what you know and demonstrate your understanding."}
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {assignment.domain ? (
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "3px 9px",
                borderRadius: 999,
                letterSpacing: "0.03em",
                whiteSpace: "nowrap",
                background: accent.pillBg,
                color: accent.pillText,
              }}>
                {assignment.domain.toUpperCase()}
              </span>
            ) : null}
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "5px 11px",
              borderRadius: 999,
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
              background: isCompleted ? "#E1F5EE" : isOverdue ? "#FEE4E2" : accent.pillBg,
              color: isCompleted ? "#0F6E56" : isOverdue ? "#B42318" : accent.bar,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
            }}>
              {isCompleted ? "Completed" : isOverdue ? "Overdue" : "Pending"}
            </span>
          </div>

          {!isCompleted && !isOverdue && timePillLabel ? (
            <span style={{
              fontSize: 13,
              fontWeight: 800,
              color: timeColor,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 0",
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}>
              <ClockSVG />
              {timePillLabel}
            </span>
          ) : null}
        </div>
      </div>

      <div style={{
        padding: "12px 18px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderTop: "1px solid #F2F4F7",
        gap: 10,
        background: "linear-gradient(90deg, rgba(248,250,252,0.9), rgba(255,255,255,0.6))",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#667085", fontWeight: 500 }}>
          <CalSVG />
          {assignment.due_date ? formatDate(assignment.due_date) : "No due date"}
        </div>

        <div style={arrowContainerStyle}>
          <ArrowSVG />
        </div>
      </div>
    </div>
  );
};

const Assignments = () => {
  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | overdue | completed
  const [dateFilter, setDateFilter] = useState("all"); // all | lastWeek | lastMonth | custom
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'start' | 'end'
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, overdue: 0, completed: 0 });
  // const [Isload, setIsLoad] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  

  
  // show intro once per logical page (Assignments) during this tab session
  const pageKey = "openingUI:assignments";
  const [showIntro, setShowIntro] = useState(() => {
    try {
      const map = window.__openingUIShownPages || {};
      return !map[pageKey];
    } catch (e) {
      return true;
    }
  });

  //    useEffect(()=>{
  //   setTimeout(()=>{
  //     setIsload(false);
  //   },2000);
  // },[]);

  useEffect(() => {
    const t = setTimeout(() => {
      setShowIntro(false);
      try {
        window.__openingUIShownPages = window.__openingUIShownPages || {};
        window.__openingUIShownPages[pageKey] = true;
      } catch (e) {}
    }, 7000);
    return () => clearTimeout(t);
  }, []);

  const { data: student, loading: studentLoading, error: studentError } =
    useGetStudentByEmailIdQuery({
      variables: { email },
      skip: !email,
    });

  useGetCampusQuery({
    variables: { id: student?.getStudentByEmailId?.campusId },
    skip: !student?.getStudentByEmailId,
  });

  const {
    data: TotalAssignments,
    loading: assignmentsLoading,
    error: assignmentsError,
    refetch: refetchAssignments,
  } =
    useGetAssignmentsForStudentQuery({
      variables: { email },
      skip: !email,
    });

  useEffect(() => {
    if (!location.state?.refetchAssignments) return;

    refetchAssignments?.();
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, refetchAssignments]);

  // Auto-redirect if student profile is incomplete
  useEffect(() => {
    if (student?.getStudentByEmailId) {
      const studentData = student.getStudentByEmailId;
      const hasName = !!studentData.name;
      const hasRollNo = !!studentData.rollNo;
      const hasLeetcodeUsername = !!studentData.leetCodeUname;
      const leetcodename = studentData.leetCodeUname || "";
      const isLeetcodeValid = leetcodename.length >= 3 && leetcodename.length <= 40;

      if (!hasName || !hasRollNo || !hasLeetcodeUsername || !isLeetcodeValid) {
        navigate("/student/my-profile?complete=required", { replace: true });
      }
    }
  }, [student, navigate]);

  // --- Date filter helper ---
  const applyDateFilter = (assignments) => {
    if (statusFilter !== "completed") return assignments;

    const now = new Date();
    let startDate, endDate;

    switch (dateFilter) {
      case "lastWeek":
        startDate = new Date();
        startDate.setDate(now.getDate() - 7);
        endDate = now;
        break;
      case "lastMonth":
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 1);
        endDate = now;
        break;
      case "custom":
        if (customRange.start && customRange.end) {
          startDate = new Date(customRange.start);
          endDate = new Date(customRange.end);
        }
        break;
      default:
        return assignments; // "all"
    }

    return assignments.filter((a) => {
      const dueDate = new Date(a.due_date);
      return startDate && endDate && dueDate >= startDate && dueDate <= endDate;
    });
  };

  // --- Main filter ---
  useEffect(() => {
    if (TotalAssignments) {
      const parseMs = (value) => {
        if (!value) return null;
        const ms = Date.parse(value);
        return Number.isNaN(ms) ? null : ms;
      };

      const now = Date.now();

      const completed =
        TotalAssignments.getAssignmentsForStudent.completedAssignments?.map((a) => {
          const startMs = parseMs(a.assignment.start_time);
          const dueMs = parseMs(a.assignment.due_date);
          const createdMs = parseMs(a.assignment.createdAt || a.assignment.created_at);
          return {
            ...a.assignment,
            completed: true,
            startMs,
            dueMs,
            createdMs,
            started: startMs === null ? true : now >= startMs,
          };
        }) || [];

      const pendingRaw =
        TotalAssignments.getAssignmentsForStudent.pendingAssignments?.map((a) => {
          const startMs = parseMs(a.assignment.start_time);
          const dueMs = parseMs(a.assignment.due_date);
          const createdMs = parseMs(a.assignment.createdAt || a.assignment.created_at);
          return {
            ...a.assignment,
            completed: false,
            startMs,
            dueMs,
            createdMs,
            started: startMs === null ? true : now >= startMs,
          };
        }) || [];

      // Only consider pending entries that have reached their start time (or have no start time)
      const startedPending = pendingRaw.filter((a) => a.started);

      const pendingOverdue = startedPending.filter((a) => {
        if (a.dueMs === null) return false;
        return now > a.dueMs;
      });

      const pendingOnTrack = startedPending.filter((a) => {
        if (a.dueMs === null) return true; // no due date, still pending
        return now <= a.dueMs;
      });

      setCounts({
        pending: pendingOnTrack.length,
        overdue: pendingOverdue.length,
        completed: completed.length,
      });

      let assignmentsToShow = [];
      const sortByCreatedDesc = (list) =>
        [...list].sort((a, b) => (b.createdMs || 0) - (a.createdMs || 0));

      if (statusFilter === "completed") {
        assignmentsToShow = sortByCreatedDesc(completed);

        const nowDate = new Date();
        if (dateFilter === "lastWeek") {
          const lastWeek = new Date();
          lastWeek.setDate(nowDate.getDate() - 7);
          assignmentsToShow = assignmentsToShow.filter((a) => {
            const due = a.dueMs ? new Date(a.dueMs) : null;
            return due && due >= lastWeek && due <= nowDate;
          });
        } else if (dateFilter === "lastMonth") {
          const lastMonth = new Date();
          lastMonth.setMonth(nowDate.getMonth() - 1);
          assignmentsToShow = assignmentsToShow.filter((a) => {
            const due = a.dueMs ? new Date(a.dueMs) : null;
            return due && due >= lastMonth && due <= nowDate;
          });
        } else if (dateFilter === "custom" && customRange.start && customRange.end) {
          const from = new Date(customRange.start);
          const to = new Date(customRange.end);
          assignmentsToShow = assignmentsToShow.filter((a) => {
            const due = a.dueMs ? new Date(a.dueMs) : null;
            return due && due >= from && due <= to;
          });
        }
      } else if (statusFilter === "overdue") {
        assignmentsToShow = sortByCreatedDesc(pendingOverdue);
      } else {
        assignmentsToShow = sortByCreatedDesc(pendingOnTrack);
      }

      setFilteredAssignments(assignmentsToShow);
    }
  }, [statusFilter, dateFilter, customRange, TotalAssignments]);




  // Show opening UI for 6s before fetching data
  if (showIntro) {
    return (
      <StudentLayout>
        <OpeningUI />
      </StudentLayout>
    );
  }

  if (studentLoading || assignmentsLoading ) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-screen">
        
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <NetworkIndicator/>
      <div className="min-h-screen w-full p-4 m-0 space-y-8 bg-white">
        {/* Heading */}
        <div className="p-6 space-y-4 text-center max-md:p-4">
          <h1 className="text-5xl font-extrabold max-md:text-4xl text-primary">
            Assignments 📚
          </h1>
          <p className="text-lg text-gray-700">
            Track your progress and stay on top of your assignments effortlessly.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 mb-8">
          {/* Status filter - underline tabs */}
          <div className="w-full">
            <div className="flex items-center gap-6 border-b border-gray-200">
              {["pending", "overdue", "completed"].map((key) => {
                const isActive = statusFilter === key;
                const label = key.charAt(0).toUpperCase() + key.slice(1);
                const countValue = counts[key] ?? 0;
                const badgeColor = key === "overdue" ? "#B42318" : key === "completed" ? "#0F6E56" : "#0B63B1";
                const badgeBg = key === "overdue" ? "#FEE4E2" : key === "completed" ? "#E1F5EE" : "#EEF2F6";
                return (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(key)}
                    className={`pb-3 text-sm font-semibold tracking-wide transition-colors border-b-2 ${
                      isActive ? "border-gray-900 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"
                    }`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                  >
                    <span>{label}</span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: isActive ? badgeColor : "#475467",
                        background: badgeBg,
                        padding: "2px 8px",
                        borderRadius: 999,
                        lineHeight: 1.2,
                      }}
                    >
                      {countValue}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date filter (only for completed) */}
          {statusFilter === "completed" && (
            <div className="relative flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-end md:gap-4">
              {/* Custom range selectors: two inputs with single-day calendar each */}
              {dateFilter === "custom" && (
                <div className="flex flex-col items-center gap-2 md:flex-row md:items-center md:gap-3">
                  {/* Start date input */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveField('start');
                        setShowCalendar((s) => activeField !== 'start' ? true : !s);
                      }}
                      className={`flex items-center gap-2 w-40 h-10 px-4 py-2 text-sm bg-white border rounded-2xl shadow-sm focus:outline-none transition-all
                        ${customRange.start ? 'border-primary' : 'border-gray-600'}
                        hover:shadow-md focus:ring-1 focus:ring-primary`}
                    >
                      <FaCalendarAlt className="text-gray-500" />
                      <span className={`${customRange.start ? 'text-gray-800' : 'text-gray-400'}`}>
                        {customRange.start ? new Date(customRange.start).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Start date'}
                      </span>
                    </button>
                    {showCalendar && activeField === 'start' && (
                      <div className="bt-datepicker-panel absolute z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px] left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-700">Select start date</span>
                          <button
                            onClick={() => setShowCalendar(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >✕</button>
                        </div>
                        <ReactDatePicker
                          selected={customRange.start ? new Date(customRange.start) : null}
                          onChange={(date) => {
                            setCustomRange((prev) => ({
                              ...prev,
                              start: date ? date.toISOString().slice(0, 10) : "",
                            }));
                            setShowCalendar(false);
                          }}
                          inline
                          calendarClassName="bt-calendar"
                        />
                      </div>
                    )}
                  </div>

                  {/* End date input */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveField('end');
                        setShowCalendar((s) => activeField !== 'end' ? true : !s);
                      }}
                      className={`flex items-center gap-2 w-40 h-10 px-4 py-2 text-sm bg-white border rounded-2xl shadow-sm focus:outline-none transition-all
                        ${customRange.end ? 'border-primary' : 'border-gray-600'}
                        hover:shadow-md focus:ring-1 focus:ring-primary`}
                    >
                      <FaCalendarAlt className="text-gray-500" />
                      <span className={`${customRange.end ? 'text-gray-800' : 'text-gray-400'}`}>
                        {customRange.end ? new Date(customRange.end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'End date'}
                      </span>
                    </button>
                    {showCalendar && activeField === 'end' && (
                      <div className="bt-datepicker-panel absolute z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px] left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-700">Select end date</span>
                          <button
                            onClick={() => setShowCalendar(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >✕</button>
                        </div>
                        <ReactDatePicker
                          selected={customRange.end ? new Date(customRange.end) : null}
                          onChange={(date) => {
                            setCustomRange((prev) => ({
                              ...prev,
                              end: date ? date.toISOString().slice(0, 10) : "",
                            }));
                            setShowCalendar(false);
                          }}
                          inline
                          calendarClassName="bt-calendar"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-1 border rounded-md"
              >
                <option value="all">All</option>
                <option value="lastWeek">Last Week</option>
                <option value="lastMonth">Last Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          )}
        </div>

        {filteredAssignments.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredAssignments.map((assignment, index) => {
              return (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  globalIndex={index}
                  onNavigate={navigate}
                />
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[500px] text-center">
            {(studentError || assignmentsError) ? (() => {
              const queryError = studentError || assignmentsError;
              const raw = queryError?.graphQLErrors?.[0]?.message || queryError?.message || "";
              const isContextCause = raw.includes(":") && !raw.includes(" ");
              const errorMessage = isContextCause
                ? raw.split(":")[1].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                : raw || "Something went wrong. Please try again later.";
              return (
                <div className="inline-flex px-4">
                  <Alert color="failure">
                    <span>{errorMessage}</span>
                  </Alert>
                </div>
              );
            })() : (
              <>
                <FaSearch className="mb-4 text-gray-400 text-7xl" />
                <h2 className="mb-2 text-2xl font-bold text-gray-600">
                  Oops, No Results Found
                </h2>
                <p className="text-gray-500 mb-6 lg:w-[500px]">
                  It looks like we couldn't find any matching data. Try refining the
                  filter options.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default Assignments;